import React, { useState } from "react";
import { XCircle, Target, ShieldAlert, Check, X, History, Briefcase, Clock, Edit2, Info } from "lucide-react";
import { useTradingContext } from "../context/TradingContext";
import { useTranslation } from "react-i18next";

// Format quantity to max 6 significant digits
const fmtQty = (val) => {
  const n = parseFloat(val);
  if (isNaN(n)) return '—';
  if (n === 0) return '0';
  // Use toPrecision(6) to limit to 6 significant digits, then remove trailing zeros
  return parseFloat(n.toPrecision(6)).toString();
};

const TPSLEdit = ({ pos, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tp, setTp] = useState(pos.take_profit || '');
  const [sl, setSl] = useState(pos.stop_loss || '');

  if (!isEditing) {
    return (
      <div className="flex flex-col gap-1 cursor-pointer group/tpsl" onClick={() => setIsEditing(true)}>
        <div className="flex items-center gap-1">
          <span className="text-[9px] text-slate-500 font-bold">TP</span>
          <span className="text-[10px] text-emerald-400 font-bold font-outfit">
            {pos.take_profit ? `$${parseFloat(pos.take_profit).toLocaleString()}` : '—'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[9px] text-slate-500 font-bold">SL</span>
          <span className="text-[10px] text-rose-400 font-bold font-outfit">
            {pos.stop_loss ? `$${parseFloat(pos.stop_loss).toLocaleString()}` : '—'}
          </span>
        </div>
        <div className="opacity-0 group-hover/tpsl:opacity-100 transition-opacity">
          <Edit2 size={9} className="text-indigo-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5 bg-slate-900 p-2 rounded-xl border border-white/10 shadow-2xl z-50 min-w-[110px]">
      <div className="flex items-center gap-1.5">
        <Target size={9} className="text-emerald-500" />
        <input
          type="number"
          value={tp}
          onChange={(e) => setTp(e.target.value)}
          className="bg-slate-950 border border-white/5 rounded px-2 py-0.5 text-[10px] w-full text-emerald-400 focus:outline-none font-outfit"
          placeholder="TP"
        />
      </div>
      <div className="flex items-center gap-1.5">
        <ShieldAlert size={9} className="text-rose-500" />
        <input
          type="number"
          value={sl}
          onChange={(e) => setSl(e.target.value)}
          className="bg-slate-950 border border-white/5 rounded px-2 py-0.5 text-[10px] w-full text-rose-400 focus:outline-none font-outfit"
          placeholder="SL"
        />
      </div>
      <div className="flex gap-1.5 justify-end mt-0.5">
        <button onClick={(e) => { e.stopPropagation(); setIsEditing(false); }} className="p-1 hover:bg-white/5 rounded text-slate-500">
          <X size={9} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onSave(tp, sl); setIsEditing(false); }}
          className="p-1 bg-indigo-500/20 hover:bg-indigo-500 text-indigo-400 hover:text-white rounded transition-all"
        >
          <Check size={9} />
        </button>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────── */
/*  POSITION CARD  (style KuCoin)                          */
/* ──────────────────────────────────────────────────────── */
const PositionCard = ({ pos, closePosition, updatePositionTPSL, currentMarketData }) => {
  const { t } = useTranslation();
  const currentPrice = currentMarketData.price || pos.entry_price;
  const isLong = pos.side === 'buy' || pos.side === 'long';
  const diff = isLong ? (currentPrice - pos.entry_price) : (pos.entry_price - currentPrice);
  const pnl = diff * pos.quantity;
  const pnlPercent = (pnl / (pos.margin || 1)) * 100;
  const liqPrice = parseFloat(pos.liquidation_price) || (isLong
    ? pos.entry_price * (1 - 1 / (pos.leverage || 1) * 0.9)
    : pos.entry_price * (1 + 1 / (pos.leverage || 1) * 0.9));

  const isPnlPos = pnl >= 0;

  return (
    <div className="border-b border-white/[0.06] last:border-b-0 px-3 py-3 hover:bg-white/[0.015] transition-colors">
      {/* ── Row 1: Header ── */}
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2">
          <span className={`text-[9px] px-1.5 py-0.5 rounded font-black tracking-wider ${isLong ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20' : 'bg-rose-500/15 text-rose-400 border border-rose-500/20'}`}>
            {isLong ? 'LONG' : 'SHORT'}
          </span>
          <span className="text-[13px] font-black text-white font-outfit tracking-tight">
            {pos.symbol || 'BTCUSDT'}
          </span>
          <span className="text-[9px] text-slate-500 font-bold">Perp</span>
          <span className="text-[9px] text-slate-400 bg-slate-800 px-1.5 py-0.5 rounded font-bold">
            {pos.leverage || 1}x
          </span>
        </div>
        <button
          onClick={() => closePosition(pos.id)}
          className="text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded border border-white/10 text-slate-400 hover:text-rose-400 hover:border-rose-500/30 hover:bg-rose-500/10 transition-all"
        >
          {t('dashboard.trades.footer.close_market')}
        </button>
      </div>

      {/* ── Row 2: PnL Banner ── */}
      <div className={`flex items-center justify-between rounded-lg px-3 py-2 mb-2.5 ${isPnlPos ? 'bg-emerald-500/8 border border-emerald-500/15' : 'bg-rose-500/8 border border-rose-500/15'}`}>
        <div>
          <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest mb-0.5">Unrealized PNL (USDT)</p>
          <div className="flex items-baseline gap-2">
            <span className={`text-[15px] font-black font-outfit ${isPnlPos ? 'text-emerald-400' : 'text-rose-400'}`}>
              {isPnlPos ? '+' : ''}{pnl.toFixed(2)}
            </span>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest mb-0.5">ROI</p>
          <span className={`text-[13px] font-black font-outfit ${isPnlPos ? 'text-emerald-400' : 'text-rose-400'}`}>
            {isPnlPos ? '+' : ''}{pnlPercent.toFixed(2)}%
          </span>
        </div>
      </div>

      {/* ── Row 3: Data Grid ── */}
      <div className="grid grid-cols-3 gap-x-2 gap-y-2">
        {/* Entry Price */}
        <div className="flex flex-col">
          <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider mb-0.5">Entry Price</span>
          <span className="text-[11px] text-slate-200 font-bold font-outfit">
            {parseFloat(pos.entry_price).toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </span>
        </div>
        {/* Mark Price */}
        <div className="flex flex-col">
          <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider mb-0.5">Mark Price</span>
          <span className="text-[11px] text-slate-200 font-bold font-outfit">
            {currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </span>
        </div>
        {/* Liq. Price */}
        <div className="flex flex-col">
          <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider mb-0.5">Liq. Price</span>
          <span className="text-[11px] text-amber-400 font-bold font-outfit">
            {liqPrice.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </span>
        </div>
        {/* Quantity */}
        <div className="flex flex-col">
          <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider mb-0.5">Quantity</span>
          <span className="text-[11px] text-slate-200 font-bold font-outfit">{fmtQty(pos.quantity)}</span>
          <span className="text-[8px] text-slate-600 font-bold">
            ≈ {(parseFloat(pos.quantity) * currentPrice).toFixed(2)} USDT
          </span>
        </div>
        {/* Margin */}
        <div className="flex flex-col">
          <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider mb-0.5">Margin</span>
          <span className="text-[11px] text-slate-200 font-bold font-outfit">
            {parseFloat(pos.margin || 0).toFixed(2)} USDT
          </span>
        </div>
        {/* TP / SL */}
        <div className="flex flex-col">
          <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider mb-0.5">TP / SL</span>
          <TPSLEdit pos={pos} onSave={(tp, sl) => updatePositionTPSL(pos.id, tp, sl)} />
        </div>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────── */
/*  ORDER ROW  (style KuCoin compact)                      */
/* ──────────────────────────────────────────────────────── */
const OrderRow = ({ order, cancelOrder }) => {
  const { t } = useTranslation();
  const isBuy = order.side === 'buy';
  const fillPct = order.quantity > 0 ? ((order.filled_quantity || 0) / order.quantity) * 100 : 0;

  return (
    <div className="border-b border-white/[0.06] last:border-b-0 px-3 py-3 hover:bg-white/[0.015] transition-colors">
      <div className="flex items-start justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded tracking-widest ${isBuy ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20' : 'bg-rose-500/15 text-rose-400 border border-rose-500/20'}`}>
            {isBuy ? t('trading.buy') : t('trading.sell')}
          </span>
          <span className="text-[11px] font-black text-white font-outfit">{order.symbol || 'BTCUSDT'}</span>
          <span className="text-[9px] text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded font-bold uppercase">{order.order_type}</span>
        </div>
        <button onClick={() => cancelOrder(order.id)} className="p-1 text-slate-600 hover:text-rose-500 transition-all hover:bg-rose-500/10 rounded">
          <XCircle size={14} strokeWidth={2} />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-x-2 gap-y-1 mb-2">
        <div className="flex flex-col">
          <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider">Price</span>
          <span className="text-[10px] text-slate-200 font-bold font-outfit">
            {order.order_type === 'market' ? 'Market' : `$${parseFloat(order.price || 0).toLocaleString()}`}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider">Qty</span>
          <span className="text-[10px] text-slate-200 font-bold font-outfit">{fmtQty(order.quantity)}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider">Date</span>
          <span className="text-[9px] text-slate-500 font-bold">
            {new Date(order.created_at).toLocaleString(undefined, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      </div>

      {/* Fill progress bar */}
      <div>
        <div className="flex justify-between text-[8px] font-bold text-slate-500 mb-1">
          <span>Filled</span>
          <span>{fillPct.toFixed(1)}% · {fmtQty(order.filled_quantity || 0)} / {fmtQty(order.quantity)}</span>
        </div>
        <div className="h-[3px] bg-slate-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-indigo-500 rounded-full shadow-[0_0_6px_rgba(99,102,241,0.5)] transition-all"
            style={{ width: `${fillPct}%` }}
          />
        </div>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────── */
/*  HISTORY ROW                                            */
/* ──────────────────────────────────────────────────────── */
const HistoryRow = ({ order }) => {
  const { t } = useTranslation();
  const isBuy = order.side === 'buy';
  const hasPnl = order.realized_pnl !== null && order.realized_pnl !== undefined;
  const pnlVal = parseFloat(order.realized_pnl);
  const isPnlPos = pnlVal >= 0;

  const statusStyles = {
    completed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    filled: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    cancelled: "bg-slate-500/10 text-slate-400 border-slate-500/20",
    pending: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    partial: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  };

  return (
    <div className="border-b border-white/[0.06] last:border-b-0 px-3 py-3 hover:bg-white/[0.015] transition-colors">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded tracking-widest ${isBuy ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20' : 'bg-rose-500/15 text-rose-400 border border-rose-500/20'}`}>
            {isBuy ? t('trading.buy') : t('trading.sell')}
          </span>
          <span className="text-[11px] font-black text-white font-outfit">{order.symbol || 'BTCUSDT'}</span>
          <span className="text-[9px] text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded font-bold uppercase">{order.order_type}</span>
        </div>
        <span className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded border tracking-widest ${statusStyles[order.status] || statusStyles.cancelled}`}>
          {t(`dashboard.trades.status.${order.status}`) || order.status}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-x-2">
        <div className="flex flex-col">
          <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider">Price</span>
          <span className="text-[10px] text-slate-200 font-bold font-outfit">
            ${parseFloat(order.price || 0).toLocaleString()}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider">Qty</span>
          <span className="text-[10px] text-slate-200 font-bold font-outfit">{fmtQty(order.filled_quantity || order.quantity)}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider">Realized PnL</span>
          {hasPnl ? (
            <span className={`text-[10px] font-black font-outfit ${isPnlPos ? 'text-emerald-400' : 'text-rose-400'}`}>
              {isPnlPos ? '+' : ''}{isNaN(pnlVal) ? '0.00' : pnlVal.toFixed(2)} USDT
            </span>
          ) : (
            <span className="text-[10px] text-slate-600 font-bold">—</span>
          )}
        </div>
      </div>

      <div className="mt-1.5">
        <span className="text-[8px] text-slate-600 font-bold">
          {new Date(order.created_at).toLocaleString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────── */
/*  EMPTY STATE                                            */
/* ──────────────────────────────────────────────────────── */
const EmptyState = ({ icon: Icon, label }) => (
  <div className="flex flex-col items-center justify-center py-12 gap-3 text-slate-700">
    <div className="w-12 h-12 rounded-full bg-slate-900 flex items-center justify-center border border-white/5">
      <Icon size={20} strokeWidth={1.5} />
    </div>
    <span className="text-[9px] font-black uppercase tracking-widest">{label}</span>
  </div>
);

/* ──────────────────────────────────────────────────────── */
/*  MAIN COMPONENT                                         */
/* ──────────────────────────────────────────────────────── */
export default function ActiveTrades() {
  const { t } = useTranslation();
  const { tradingMode, orders, positions, cancelOrder, closePosition, updatePositionTPSL, currentMarketData } = useTradingContext();
  const [activeTab, setActiveTab] = useState(tradingMode === 'futures' ? "positions" : "orders");

  const openOrders = orders.filter(o => o.status === 'pending' || o.status === 'partial');
  const closedOrders = orders.filter(o => o.status === 'completed' || o.status === 'cancelled' || o.status === 'filled');

  const tabs = [
    ...(tradingMode === 'futures' ? [{ id: 'positions', icon: Briefcase, label: t('dashboard.trades.tabs.positions'), count: positions.length }] : []),
    { id: 'orders', icon: Clock, label: t('dashboard.trades.tabs.orders'), count: openOrders.length },
    { id: 'history', icon: History, label: t('dashboard.trades.tabs.history'), count: closedOrders.length },
  ];

  return (
    <div className="flex flex-col h-full bg-transparent overflow-hidden" style={{ fontFamily: "'Inter', 'Outfit', sans-serif" }}>

      {/* ── Tab Bar ── */}
      <div className="flex items-center border-b border-white/[0.07] bg-slate-950/30 px-1 shrink-0">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`relative flex items-center gap-1.5 px-3 py-2.5 text-[9px] font-black uppercase tracking-widest transition-all ${
              activeTab === tab.id ? 'text-white' : 'text-slate-500 hover:text-slate-300'
            }`}
          >
            <tab.icon size={11} className={activeTab === tab.id ? 'text-indigo-400' : ''} />
            {tab.label}
            {tab.count > 0 && (
              <span className={`text-[8px] font-black px-1 py-0.5 rounded-full ${activeTab === tab.id ? 'bg-indigo-500/20 text-indigo-400' : 'bg-slate-800 text-slate-500'}`}>
                {tab.count}
              </span>
            )}
            {activeTab === tab.id && (
              <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-indigo-500 rounded-full shadow-[0_0_8px_rgba(99,102,241,0.6)]" />
            )}
          </button>
        ))}
      </div>

      {/* ── Content ── */}
      <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.05) transparent' }}>
        {activeTab === 'positions' && (
          positions.length === 0
            ? <EmptyState icon={Briefcase} label={t('dashboard.trades.empty.positions')} />
            : positions.map(pos => (
              <PositionCard
                key={pos.id}
                pos={pos}
                closePosition={closePosition}
                updatePositionTPSL={updatePositionTPSL}
                currentMarketData={currentMarketData}
              />
            ))
        )}

        {activeTab === 'orders' && (
          openOrders.length === 0
            ? <EmptyState icon={Clock} label={t('dashboard.trades.empty.orders')} />
            : openOrders.map(order => (
              <OrderRow key={order.id} order={order} cancelOrder={cancelOrder} />
            ))
        )}

        {activeTab === 'history' && (
          closedOrders.length === 0
            ? <EmptyState icon={History} label={t('dashboard.trades.empty.history')} />
            : closedOrders.map(order => (
              <HistoryRow key={order.id} order={order} />
            ))
        )}
      </div>

      {/* ── Footer ── */}
      <div className="px-3 py-1.5 bg-slate-950/40 border-t border-white/[0.06] flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_5px_rgba(16,185,129,0.6)]" />
            <span className="text-[8px] font-black text-slate-600 uppercase tracking-widest">{t('dashboard.trades.footer.gateway')}</span>
          </div>
          <div className="flex items-center gap-1">
            <Info size={9} className="text-indigo-400" />
            <span className="text-[8px] font-black text-slate-600 uppercase tracking-widest">{t('dashboard.trades.footer.latency')}: 42ms</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1 text-[8px] font-black text-slate-700 uppercase tracking-widest">
            <Check size={8} className="text-emerald-500" /> {t('dashboard.trades.footer.secure')}
          </span>
        </div>
      </div>
    </div>
  );
}